import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Briefcase, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import professionalProfileApi from "../../../api/professionalProfile";
import ClampedText from "../../../components/common/ClampedText";
import CustomLoader from "../../../components/common/CustomLoader";
import OptionDisplay from "../../../components/common/OptionDisplay";
import EmptyStateCard from "../../../components/professional/EmptyStateCard";
import Modal from "../../../components/professional/Modal";
import AviationCategoryForm from "./AviationCategoryForm";

const AviationCategoryTab = ({
  aviationCategories,
  onModification,
  setNotification,
  dropDownOptions,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleOpen = (aviationCategory = null) => {
    setSelectedCategory(aviationCategory);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedCategory(null);
    setOpen(false);
  };

  const handleSave = async (data) => {
    setLoading(true);
    try {
      let payload = { ...data };

      if (selectedCategory && payload.id) {
        const res = await professionalProfileApi.updateSingleResource(
          data.id,
          "aviation-category",
          payload
        );
        if (res.status === 200) {
          onModification();
          setNotification("Aviation category updated successfully", "success");
        }
      } else {
        delete payload.id;
        const res = await professionalProfileApi.updateProfessionalProfile({
          aviation_categories: [payload],
        });
        if (res.status === 200 || res.status === 201) {
          onModification();
          setNotification("Aviation category added successfully", "success");
        }
      }
      handleClose();
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.aviation_categories)
        return setErrors(error?.response?.data?.aviation_categories[0]);
      setNotification(
        "An error occurred while updating aviation category",
        "error"
      );
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CustomLoader />;

  const dialogTitle = selectedCategory
    ? "Edit Aviation Category"
    : "Add Aviation Category";

  return (
    <div>
      {/* Empty State */}
      {aviationCategories?.length === 0 ? (
        <EmptyStateCard
          icon={<Briefcase size={48} color="#1976d2" />}
          title="No aviation category added yet"
          description="Add your aviation category to better reflect your professional qualifications and expertise."
          buttonText="Add Aviation Category"
          handleOpen={handleOpen}
        />
      ) : (
        <>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              mb: 3,
            }}
          >
            <Typography variant="h6">Aviation Categories</Typography>
            <Button
              startIcon={<Plus size={18} />}
              variant="contained"
              color="primary"
              onClick={() => handleOpen()}
            >
              Add Aviation Category
            </Button>
          </Box>

          {/* Category Cards */}
          <Grid container spacing={3}>
            {aviationCategories.map((cat) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                    transition: "0.2s",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.15)" },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <CardContent>
                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} /> */}
                    <ClampedText color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Flight Crew:&nbsp;</strong>
                      <OptionDisplay
                        options={dropDownOptions.flightCrewChoices}
                        value={cat.flight_crew}
                      />
                    </ClampedText>
                    {/* </Box> */}

                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} /> */}
                    <ClampedText color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Cabin / Inflight:&nbsp;</strong>
                      <OptionDisplay
                        options={dropDownOptions.cabinInflightChoices}
                        value={cat.cabin_inflight}
                      />
                    </ClampedText>
                    {/* </Box> */}

                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} /> */}
                    <ClampedText color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Flight Ops / Dispatch:&nbsp;</strong>
                      <OptionDisplay
                        options={dropDownOptions.flightOpsDispatchChoices}
                        value={cat.flight_ops_dispatch}
                      />
                    </ClampedText>
                    {/* </Box> */}

                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} /> */}
                    <ClampedText color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Air Traffic Services:&nbsp;</strong>
                      <OptionDisplay
                        options={dropDownOptions.airTrafficServiceChoices}
                        value={cat.air_traffic_services}
                      />
                    </ClampedText>
                    {/* </Box> */}
                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} /> */}
                    <ClampedText color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Medical:&nbsp;</strong>
                      <OptionDisplay
                        options={dropDownOptions.medicalChoices}
                        value={cat.medical}
                      />
                    </ClampedText>
                    {/* </Box> */}

                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} /> */}
                    <ClampedText color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Management & Regulatory:&nbsp;</strong>
                      <OptionDisplay
                        options={dropDownOptions.managementRegulatoryChoices}
                        value={cat.management_regulatory}
                      />
                    </ClampedText>
                    {/* </Box> */}
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    <IconButton color="primary" onClick={() => handleOpen(cat)}>
                      <Pencil size={18} />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Modal handleClose={handleClose} open={open} title={dialogTitle}>
        <AviationCategoryForm
          onSubmit={handleSave}
          onCancel={handleClose}
          aviationCategory={selectedCategory}
          errors={errors}
          setErrors={setErrors}
          dropDownOptions={dropDownOptions}
        />
      </Modal>
    </div>
  );
};

export default AviationCategoryTab;
